import { Link } from 'react-router-dom';
import {
  FaArrowRight,
  FaHandsHelping,
  FaInfoCircle,
  FaMapMarkedAlt,
  FaQuestionCircle,
  FaUserClock,
} from 'react-icons/fa';
import HomeHeroSection from '../components/home/HomeHeroSection';
import SectionHeading from '../components/SectionHeading';
import {
  coreServices,
  eligibilityNotes,
  faqs,
  intakeSteps,
} from '../constants/homeContent';

export default function HomePage() {
  return (
    <>
      <HomeHeroSection />

      <section className="py-20">
        <div className="mero-wrapper">
          <SectionHeading
            eyebrow="Support At Home"
            title="Core services families request most"
            description="Our team provides practical and compassionate in-home support for participants who are eligible for personal assistance and related home care programs."
          />

          <div className="mt-8 grid gap-5 md:grid-cols-2 lg:grid-cols-4">
            {coreServices.map((item) => {
              const Icon = item.icon;

              return (
                <article
                  key={item.title}
                  className="group rounded-xl border border-slate-200 bg-white p-6 shadow-sm transition hover:-translate-y-1 hover:shadow-md"
                >
                  <span className="inline-flex h-10 w-10 items-center justify-center rounded-lg bg-teal-100 text-teal-700 transition group-hover:bg-teal-700 group-hover:text-white">
                    <Icon />
                  </span>
                  <p className="mt-3 font-semibold text-teal-800">{item.title}</p>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      <section className="bg-white py-20">
        <div className="mero-wrapper">
          <SectionHeading
            eyebrow="Getting Started"
            title="How enrollment and onboarding usually works"
            description="A clear intake process helps families move forward with confidence and fewer delays."
          />

          <div className="mt-10 grid gap-5 md:grid-cols-2 xl:grid-cols-4">
            {intakeSteps.map((step, index) => {
              const Icon = step.icon;

              return (
                <article
                  key={step.title}
                  className="rounded-2xl border border-slate-200 bg-slate-50 p-6"
                >
                  <div className="flex items-center justify-between">
                    <span className="inline-flex h-10 w-10 items-center justify-center rounded-lg bg-teal-100 text-teal-700">
                      <Icon />
                    </span>
                    <span className="text-xs font-bold uppercase tracking-[0.18em] text-slate-400">
                      Step {index + 1}
                    </span>
                  </div>
                  <h3 className="mt-4 text-lg font-semibold text-slate-900">{step.title}</h3>
                  <p className="mt-2 text-sm leading-6 text-slate-600">{step.text}</p>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-20">
        <div className="mero-wrapper grid gap-8 lg:grid-cols-[1.15fr_0.85fr]">
          <article className="rounded-3xl border border-slate-200 bg-white p-8 shadow-sm">
            <SectionHeading
              eyebrow="Eligibility Snapshot"
              title="Questions families should ask before starting"
              description="These planning points are based on common home-care coverage patterns and can help you prepare better intake conversations."
            />

            <ul className="mt-8 space-y-4 text-sm leading-6 text-slate-700">
              {eligibilityNotes.map((note) => (
                <li key={note} className="flex items-start gap-3">
                  <FaInfoCircle className="mt-1 text-teal-700" />
                  <span>{note}</span>
                </li>
              ))}
            </ul>
          </article>

          <aside className="rounded-3xl border border-slate-200 bg-slate-900 p-8 text-slate-100">
            <h3 className="text-xl font-bold text-white">Service area and response</h3>
            <p className="mt-3 text-sm leading-7 text-slate-300">
              We support participants in Harrisburg and nearby communities with
              coordinated scheduling and family communication.
            </p>

            <div className="mt-6 space-y-3 text-sm">
              <p className="inline-flex items-center gap-2">
                <FaMapMarkedAlt className="text-cyan-300" />
                Greater Harrisburg and surrounding areas
              </p>
              <p className="inline-flex items-center gap-2">
                <FaUserClock className="text-cyan-300" />
                Intake response during business hours
              </p>
              <p className="inline-flex items-center gap-2">
                <FaHandsHelping className="text-cyan-300" />
                Ongoing family and participant coordination
              </p>
            </div>

            <Link
              to="/resources"
              className="mt-7 inline-flex items-center gap-2 rounded-full border border-white/25 bg-white/10 px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-white/20"
            >
              Explore Family Resources
              <FaArrowRight />
            </Link>
          </aside>
        </div>
      </section>

      <section className="bg-white py-20">
        <div className="mero-wrapper">
          <SectionHeading
            eyebrow="FAQ"
            title="Frequently asked questions from families"
            description="Quick answers to common concerns before starting care."
          />

          <div className="mt-8 grid gap-4">
            {faqs.map((item) => (
              <article
                key={item.question}
                className="rounded-2xl border border-slate-200 bg-slate-50 p-6"
              >
                <h3 className="inline-flex items-center gap-2 text-lg font-semibold text-slate-900">
                  <FaQuestionCircle className="text-teal-700" />
                  {item.question}
                </h3>
                <p className="mt-2 text-sm leading-7 text-slate-600">{item.answer}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-white py-20">
        <div className="mero-wrapper grid gap-6 rounded-3xl border border-slate-200 bg-slate-50 p-8 lg:grid-cols-[1fr_auto] lg:items-center">
          <div>
            <h2 className="inline-flex items-center gap-2 text-2xl font-bold text-slate-900">
              <FaHandsHelping className="text-teal-700" />
              Need help understanding eligibility?
            </h2>
            <p className="mt-3 text-slate-600">
              We can walk your family through qualification requirements,
              referral documents, and the first steps for beginning service.
            </p>
          </div>

          <Link
            to="/contact"
            className="rounded-full bg-teal-700 px-6 py-3 text-sm font-semibold text-white transition hover:bg-teal-800"
          >
            Talk to Our Team
          </Link>
        </div>
      </section>
    </>
  );
}
